<template>
  <teleport to="body">
    <modal-base
      :show="loginStore.showLoginModal"
      for-login
      hide-submit
      hide-cancel
      :hide-close="preventClose || usuarioFicticio"
      :title="modalTitle"
      @on-close="handleClose">
      <template #backAction>
        <span
          v-show="show !== 'initial' && !preventClose && !usuarioFicticio"
          class="text-primary bold"
          style="cursor: pointer"
          @click="handleBackClick()">
          <i class="fa-solid fa-chevron-left me-2 d-inline"></i>
        </span>
      </template>
      <template v-if="isUsuariosHomologados && show === 'initial'" #header>
        <span class="text-start title h6 text-primary">
          {{ $t('header.login.iniciaSesionO') }}
          <span class="text-decoration-underline">{{ $t('header.login.solicitaAcceso') }}</span>
          <InfoIcon
            size="14px"
            class="ms-1"
            placement="top"
            color="var(--bs-primary)"
            :message-info="$t('header.login.solicitaAccesoInfo', { nombre: agenciaStore.nombre })" />
        </span>
      </template>
      <template #body>
        <div class="container-login text-left">
          <template v-if="show === 'initial'">
            <div class="title-field text-primary mb-2">
              <span>
                {{
                  isUsuariosHomologados
                    ? $t('home.form.personalInformation.emailLoginHomologados')
                    : $t('home.form.personalInformation.emailLogin')
                }}
              </span>
            </div>
            <form autocomplete="on" @submit.prevent="validateEmail(informacionLogin.email)">
              <input
                id="email"
                v-model="informacionLogin.email"
                type="email"
                class="text-primary email-input form-control text-start"
                :placeholder="$t('home.form.personalInformation.email')"
                autocomplete="email"
                name="email"
                required />
              <div class="text-center my-2 my-md-3 mt-md-3">
                <MyGocuButton
                  type="submit"
                  class="w-100 m-auto"
                  :label="$t('home.form.personalInformation.continuar')" />
              </div>
            </form>
            <div
              v-if="!isBodoSound && !isMandarinaSports && isGoogleOauthEnabled && !isUsuariosHomologados"
              class="divider">
              <span class="divider-text">o</span>
            </div>
            <div
              v-if="!isBodoSound && !isMandarinaSports && isGoogleOauthEnabled && !isUsuariosHomologados"
              class="text-center mt-2 mt-md-3">
              <button
                class="btn btn-primary w-100 google-btn d-flex justify-content-center align-items-center m-auto"
                @click="doGoogleOauthLogin">
                <img src="@/assets/img/logo_google.svg" alt="Logo de Google" class="me-3" />
                <span class="text-primary semibold text-mobile">
                  {{ $t('header.login.continuarGoogle') }}
                </span>
              </button>
            </div>
          </template>
          <template v-else-if="show === 'registerGoogle'">
            <RegistroGoogleForm
              :informacion-registro-google="informacionRegistroGoogle"
              @usuario-logueado="closeModal()" />
          </template>
          <template v-else>
            <LoginForm
              v-if="!showRecordar"
              :email="informacionLogin.email"
              :show-register="show == 'register'"
              :usuario-ficticio="usuarioFicticio"
              @mostrar-login="mostrarLogin"
              @mostrar-recuperar-password="resetPassword"
              @usuario-logueado="closeModal()" />

            <UsuarioPasswordRecordar
              v-else
              :mail-reset="mailReset"
              from-login
              @volver-al-login="show = 'initial'"
              @password-reset="closeModal()" />
          </template>
        </div>
      </template>
    </modal-base>
  </teleport>
</template>

<script setup>
  import ModalBase from '@/components/modales/ModalBase';
  import { useLoginStore } from '@/store_pinia/login';
  import { computed, reactive, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import router from '@/router';
  import UsuarioApiService from '../../services/api/UsuarioApiService';
  import LoginForm from '@/components/form/LoginForm.vue';
  import { useI18n } from 'vue-i18n';
  import { controlErrorRegistrarLoginUsuario } from '@/services/utils/ErrorControl';
  import { googleSdkLoaded } from 'vue3-google-login';
  import {
    alertAutoCloseWarningWithoutCallback,
    alertInfoHtml,
    alertWarning,
  } from '@/services/utils/AlertUtils';
  import UsuarioPasswordRecordar from '@/views/usuario/UsuarioPasswordRecordar/UsuarioPasswordRecordar.vue';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import { useUserStore } from '@/store_pinia/userStore';
  import { agenciaStoreModule } from '@/store_pinia/agencia';
  import { createToaster } from '@meforma/vue-toaster';
  import RegistroGoogleForm from '@/components/form/RegistroGoogleForm.vue';
  import MyGocuButton from '@/components/common/buttons/MyGocuButton.vue';
  import InfoIcon from '@/components/Icons/InfoIcon.vue';
  import { clearObject } from '@/helpers/utils';

  const props = defineProps({
    modalActive: {
      type: Boolean,
      default: null,
    },
    preventClose: {
      type: Boolean,
      default: false,
    },
  });

  const { t } = useI18n();

  const route = useRoute();
  const loginStore = useLoginStore();
  const userStore = useUserStore();
  const loadingStore = useLoadingStore();
  const agenciaStore = agenciaStoreModule();
  const toaster = createToaster({ position: 'top-right' });

  const isBodoSound = ref(window.location.hostname.includes('bodosound'));
  const isMandarinaSports = ref(window.location.hostname.includes('mandarinasports'));
  const showRecordar = ref(false);

  const modalTitle = ref(t('header.login.welcome'));

  const informacionLogin = reactive({
    email: null,
    password: null,
  });

  const informacionRegistroGoogle = reactive({
    email: null,
    nombre: null,
    apellidos: null,
    fechaNacimiento: null,
    telefono: null,
  });

  const isGoogleOauthEnabled = computed(() => agenciaStore.getGoogleOauth);
  const isUsuariosHomologados = computed(() => agenciaStore.usuariosHomologados);

  const show = ref('initial');
  const mailReset = ref();
  const usuarioFicticio = ref(null);

  async function loadUsuarioFicticioData(uuid) {
    if (!uuid) return;

    try {
      loadingStore.show();
      const response = await UsuarioApiService.getRegistroAndLoginFicticio(uuid);
      usuarioFicticio.value = { ...response, uuid: uuid };

      if (response.email) {
        informacionLogin.email = response.email;
        show.value = 'register';
        setModalTitle('register');
      }
    } catch (error) {
      toaster.error(t('home.form.personalInformation.errorUsuarioFicticio'));
      console.error('Error al cargar datos ficticio:', error);
    } finally {
      loadingStore.hide();
    }
  }

  function closeModal() {
    show.value = 'initial';
    showRecordar.value = false;
    informacionLogin.email = null;
    const redirectRoute = loginStore.redirectAfterLogin;
    loginStore.closeLoginModal();
    if (redirectRoute && userStore.isLogged) {
      router.push(redirectRoute);
    }
  }

  async function validateEmail(email) {
    try {
      loadingStore.show();
      const { exists, activo, activoPending } = await UsuarioApiService.validateEmail(email);
      if (exists && !activo) {
        if (activoPending) {
          await alertInfoHtml(t('header.login.solicitudEnRevision'));
        } else {
          await alertWarning(t('header.login.solicitudRechazada'));
        }
        return;
      }
      const vista = exists ? 'login' : 'register';
      show.value = vista;
      setModalTitle(vista);
    } catch (error) {
      console.error(error);
      show.value = 'register';
    } finally {
      loadingStore.hide();
    }
  }

  function handleBackClick() {
    showRecordar.value = false;
    show.value = 'initial';
    setModalTitle();
  }

  function mostrarLogin() {
    setModalTitle('homeLogin');
    show.value = 'initial';
  }

  function resetPassword(email) {
    setModalTitle('resetPassword');
    mailReset.value = email;
    showRecordar.value = true;
  }

  function setModalTitle(modalType) {
    switch (modalType) {
      case 'homeLogin':
        modalTitle.value = t('header.login.welcome');
        break;
      case 'login':
        modalTitle.value = t('home.form.personalInformation.welcomeBack');
        break;
      case 'register':
        modalTitle.value = t('header.login.tusDatos');
        break;
      case 'registerGoogle':
        modalTitle.value = t('header.login.tusDatos');
        break;
      case 'resetPassword':
        modalTitle.value = t('header.login.restablecerPassword');
        break;
      default:
        modalTitle.value = t('header.login.welcome');
        break;
    }
  }

  function doGoogleOauthLogin() {
    googleSdkLoaded(google => {
      google.accounts.oauth2
        .initCodeClient({
          client_id: agenciaStore.getGoogleOauth,
          scope:
            'email profile openid https://www.googleapis.com/auth/user.birthday.read https://www.googleapis.com/auth/user.phonenumbers.read',
          callback: response => regAndLoginGoogle(response),
        })
        .requestCode();
    });
  }

  async function regAndLoginGoogle(callbackResponse) {
    try {
      loadingStore.show();
      const response = await UsuarioApiService.loguearGoogle(callbackResponse);

      if (response.token != null) {
        userStore.login(response.token);
        toaster.success(t('header.login.msg.loginSuccess'));
        closeModal();
      } else if (
        response.nombre != null &&
        response.apellidos != null &&
        response.email != null &&
        response.fechaNacimiento != null &&
        response.telefono != null
      ) {
        const res = await UsuarioApiService.registrarYLoguearGoogle(response);
        if (res.token == null) {
          alertAutoCloseWarningWithoutCallback(3000, t('errors.inesperado'));
        } else {
          userStore.login(res.token);
          toaster.success(t('header.login.msg.loginSuccess'));
          closeModal();
        }
      } else {
        clearObject(informacionRegistroGoogle);
        Object.assign(informacionRegistroGoogle, response);

        show.value = 'registerGoogle';
        setModalTitle('registerGoogle');
      }
    } catch (error) {
      console.error(error);
      controlErrorRegistrarLoginUsuario(error);
    } finally {
      loadingStore.hide();
    }
  }

  function handleClose() {
    if (!props.preventClose) {
      loginStore.closeLoginModal();
    }
  }

  //El LoginModal se carga en el TheHeader, antes de cargar la vista principal, por lo tanto la route se carga posteriormente
  watch(
    () => route.params.uuid,
    uuid => {
      if (uuid) {
        loadUsuarioFicticioData(uuid);
      }
    },
    { immediate: true }
  );
</script>

<style lang="scss" scoped>
  :deep(.modal-container) {
    min-width: unset !important;
    max-width: unset !important;
    width: 758px !important;
    padding: 20px !important;
  }

  :deep(.modal-body) {
    margin: 20px 0 0 0 !important;
  }

  :deep(.title) {
    @media only screen and (max-width: 768px) {
      font-size: 16px;
    }
  }

  .container-login {
    border-radius: var(--bs-border-radius-xl) !important;

    @media only screen and (max-width: 768px) {
      padding: 0;
    }

    .title {
      font-size: 20px;
      font-weight: 500;
      letter-spacing: 0.309px;
    }

    .title-field {
      font-size: 14px;
      font-weight: 400;
      @media only screen and (max-width: 768px) {
        font-size: 12px;
      }
    }

    .divider {
      text-align: center;
      position: relative;
      opacity: 50%;
    }

    .divider-text {
      background-color: white;
      padding: 0 10px;
      position: relative;
      z-index: 1;
      color: var(--bs-primary);
      opacity: 100%;
    }

    .divider::after {
      content: '';
      background: var(--bs-primary);
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 1px;
      z-index: 0;
    }
  }

  .login {
    max-width: 550px;
    margin: auto;
  }

  .register {
    max-width: 850px;
    margin: auto;
  }

  .cambio-vista {
    cursor: pointer;
    transition: 0.3s ease-in-out;
    color: white;

    &:hover {
      color: var(--bs-secondary);
    }
  }

  .cambio-vista-2 {
    cursor: pointer;
    transition: 0.3s ease-in-out;

    &:hover {
      color: var(--bs-secondary);
    }
  }

  .form-check-input:checked[type='checkbox'],
  .form-check-input[type='checkbox'] {
    border-radius: 0;
  }

  .email-input {
    box-shadow: 0 0 3px 0 rgba(var(--bs-primary-rgb), 0.35) inset !important;
    border-radius: var(--bs-border-radius) !important;
    font-size: 16px;

    @media only screen and (max-width: 768px) {
      font-size: 12px;
    }
  }

  .google-btn {
    border: 1px solid var(--bs-primary) !important;
    box-shadow: none !important;
    background-color: transparent !important;
    transition: all 0.3s ease;
    @media only screen and (max-width: 768px) {
      font-size: 14px !important;
    }
  }

  .google-btn:hover {
    background-color: var(--bs-primary) !important;

    span {
      transition: all 0.5s ease;
      color: #ffffff !important;
    }
  }

  .g-btn-wrapper {
    width: 100% !important;
  }

  .text-mobile {
    @media only screen and (max-width: 768px) {
      font-size: 14px !important;
      font-weight: 600 !important;
    }
  }
</style>
